<!-- AppBar.vue -->
<template>
  <v-app-bar app color="primary" :height="appBarHeight">
    <v-container fluid class="app-bar-container px-4 py-0">
      <v-row align="center" no-gutters class="app-bar-row">
        <v-col cols="auto" class="d-flex justify-start">
          <img
            :src="logoImage"
            alt="AI세특 로고"
            class="app-bar-logo"
            @click="goToMain"
            style="cursor: pointer;"
          />
        </v-col>

        <v-col class="d-flex justify-start title-col">
          <v-toolbar-title class="title-responsive" @click="goToMain" style="cursor: pointer;">
            {{ appTitle }}
          </v-toolbar-title>
        </v-col>

        <div class="app-bar-actions">
          <v-btn
            v-if="user"
            variant="text"
            @click="goToAccount"
            class="credit-display"
          >
            <svg-icon type="mdi" :path="icons.mdiCashMultiple" :size="iconSize" left />
            <span class="main-credit-font">{{ formattedCredit }}</span>
          </v-btn>

          <v-btn
            v-if="user && !isASGS"
            variant="text"
            @click="goToPayment"
            class="kakao-signage"
          >
            <v-img
              src="@/assets/kakaopay_signage.png"
              alt="카카오페이_사이니지"
              :width="50"
              :max-height="40"
              contain
            />
          </v-btn>

          <v-btn icon @click="emitOpenUsageGuide" class="d-none d-sm-flex">
            <svg-icon type="mdi" :path="icons.mdiHelpCircle" :size="iconSize" color="white" />
          </v-btn>

          <v-btn icon @click="openYouTubeGuide" aria-label="유튜브" class="d-none d-sm-flex">
            <svg-icon type="mdi" :path="icons.mdiYoutube" :size="iconSize + 8" color="red" />
          </v-btn>

          <v-btn icon class="d-none d-md-flex" @click="openKakaoTalk" aria-label="카카오톡">
            <v-img src="@/assets/kakaotalk_btn.png" :width="iconSize" :height="iconSize" contain />
          </v-btn>

          <v-btn icon class="d-none d-md-flex" @click="openNaverCafe" aria-label="네이버 카페">
            <v-img src="@/assets/naver_cafe_icon.png" :width="iconSize" :height="iconSize" contain />
          </v-btn>

          <v-btn v-if="!user && !isInApp" icon aria-label="로그인" @click="emitSignInWithGoogle">
            <svg-icon type="mdi" :path="icons.mdiLogin" :size="iconSize" color="white" />
          </v-btn>

          <v-btn v-if="user" icon aria-label="설정" @click="emitToggleDrawer">
            <svg-icon type="mdi" :path="icons.mdiCog" :size="iconSize" color="white" />
          </v-btn>

          <v-menu v-if="user" location="bottom end" :offset="10" :close-on-content-click="true" content-class="avatar-menu">
            <template #activator="{ props }">
              <v-btn icon aria-label="사용자 메뉴" v-bind="props">
                <v-avatar :size="iconSize + 8" color="white">
                  <v-img v-if="avatarImageUrl" :src="avatarImageUrl" alt="User Profile Picture" cover />
                  <span v-else class="text-primary font-weight-bold">{{ avatarFallback }}</span>
                </v-avatar>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :title="userInfo?.nickname || user.displayName || '사용자'" :subtitle="user.email" />

              <v-divider />

              <v-list-item title="사용자 정보" @click="emitOpenUserInfoForm" />
              <v-list-item title="로그아웃" @click="emitSignOut" />
              <v-list-item title="회원 탈퇴" @click="emitWithdrawMembership" />

              <v-divider />

              <v-list-item title="이용 안내" @click="emitOpenUsageGuide" />
              <v-list-item title="사용법 문의(카톡 1:1 오픈채팅)" @click="openKakaoTalk" />
              <v-list-item title="단체구매 문의(카톡 1:1 오픈채팅)" @click="openKakaoTalk" />
              <v-list-item title="오늘의 공지" @click="emitShowNoticeDialog" />
            </v-list>
          </v-menu>
        </div>
      </v-row>
    </v-container>
  </v-app-bar>
</template>

<script>
import { isKakaoNaverInAppBrowser } from '@/utils/browserUtils';
import icons from '@/icons';
import responsiveMixin from '@/mixins/responsiveMixin';

export default {
  name: 'AppBar',
  props: {
    logoImage: {
      type: String,
      default: '',
    },
    user: {
      type: Object,
      default: null,
    },
    userInfo: {
      type: Object,
      default: null,
    },
    formattedCredit: {
      type: String,
      default: '0',
    },
  },
  mixins: [responsiveMixin],
  emits: [
    'signInWithGoogle',
    'signOut',
    'toggleDrawer',
    'openUsageGuide',
    'openUserInfoForm',
    'withdrawMembership',
    'showNoticeDialog',
  ],
  data() {
    return {
      isInApp: false,
      icons,
    };
  },
  mounted() {
    this.isInApp = isKakaoNaverInAppBrowser();
  },
  computed: {
    appBarHeight() {
      return this.isXs || this.isSm ? 56 : 64;
    },
    iconSize() {
      if (this.isXs) {
        return 20;
      }
      if (this.isSm) {
        return 24;
      }
      return 28;
    },
    appTitle() {
      return this.isXs ? 'AI세특' : 'AI세특 - 세특 써주는 AI';
    },
    avatarImageUrl() {
      return this.user?.photoURL || '';
    },
    avatarFallback() {
      return (this.userInfo?.nickname || this.user?.displayName || this.user?.email || '?').trim().charAt(0).toUpperCase();
    },
  },
  methods: {
    goToMain() {
      if (this.$route.path !== '/app') {
        this.$router.push('/app');
      }
    },
    openKakaoTalk() {
      window.open('https://open.kakao.com/o/sTf1el7g', '_blank');
    },
    openNaverCafe() {
      window.open('https://cafe.naver.com/studentremarkhelper', '_blank');
    },
    openYouTubeGuide() {
      window.open('https://www.youtube.com/watch?v=d4RXl2t4zFU&list=PLpG7Ld9Vh1o2KoAtFq1Lgc_wh4O5OUUIz', '_blank');
    },
    emitSignInWithGoogle() {
      this.$emit('signInWithGoogle');
    },
    emitSignOut() {
      this.$emit('signOut');
    },
    emitToggleDrawer() {
      this.$emit('toggleDrawer');
    },
    goToAccount() {
      if (this.$route.path !== '/account') {
        this.$router.push('/account');
      }
    },
    goToPayment() {
      if (this.$route.path !== '/payment') {
        this.$router.push('/payment');
      }
    },
    emitOpenUsageGuide() {
      this.$emit('openUsageGuide');
    },
    emitOpenUserInfoForm() {
      this.$emit('openUserInfoForm');
    },
    emitWithdrawMembership() {
      this.$emit('withdrawMembership');
    },
    emitShowNoticeDialog() {
      this.$emit('showNoticeDialog');
    },
  },
};
</script>

<style scoped>
.v-app-bar {
  max-width: 100%;
  overflow: hidden;
}

.app-bar-container,
.app-bar-row,
.title-col {
  min-width: 0;
}

.app-bar-row {
  flex-wrap: nowrap;
}

.title-col {
  flex: 1 1 auto;
}

.app-bar-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
}

.app-bar-actions > * {
  flex-shrink: 0;
}

.app-bar-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  display: block;
  margin-right: 5px;
}

.title-responsive {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.main-credit-font {
  font-size: 1.2em;
  line-height: 1;
}

.credit-display {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  min-width: auto;
  padding-left: 8px;
  padding-right: 8px;
}

.kakao-signage {
  min-width: auto;
  padding-left: 6px;
  padding-right: 6px;
}

.avatar-menu {
  z-index: 2000 !important;
}

@media (max-width: 959px) {
  .main-credit-font {
    font-size: 0.9em;
  }

  .v-app-bar {
    position: fixed;
    width: 100vw;
    max-width: 100vw;
  }

  .app-bar-logo {
    width: 30px;
    height: 30px;
    margin-right: 2px;
  }

  .title-responsive {
    font-size: 1rem;
  }

  .app-bar-actions {
    gap: 0;
  }

  .kakao-signage {
    display: none;
  }
}

@media (min-width: 960px) and (max-width: 1263px) {
  .title-responsive {
    font-size: 1.2rem;
  }
}
</style>





